import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Truck, CheckCircle, XCircle, Clock } from 'lucide-react';
import { getOrder, approveQuote } from '../services/api';
import { differenceInMinutes } from 'date-fns';

export default function ApproveQuote() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [approved, setApproved] = useState(false);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId!),
    enabled: !!orderId,
  });

  const approveMutation = useMutation({
    mutationFn: () => approveQuote(orderId!, token),
    onSuccess: () => {
      setApproved(true);
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uber-green"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Order Not Found</h1>
          <p className="text-gray-600">This quote link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  const latestQuote = order.quotes?.[0];
  const isExpired = latestQuote && new Date(latestQuote.expiresAt) < new Date();
  const minutesRemaining = latestQuote
    ? differenceInMinutes(new Date(latestQuote.expiresAt), new Date())
    : 0;

  if (approved) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-uber-green mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Delivery Confirmed!</h1>
          <p className="text-gray-600 mb-4">
            Your delivery has been booked. A driver is on their way to pick up your
            package.
          </p>
          <p className="text-gray-600">
            You'll receive text updates about your delivery status.
          </p>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Quote Expired</h1>
          <p className="text-gray-600 mb-4">
            This delivery quote has expired. Please contact the store to request a
            new quote.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-6">
            <Truck className="w-12 h-12 text-uber-green mx-auto mb-2" />
            <h1 className="text-2xl font-bold">Approve Delivery</h1>
            <p className="text-gray-600">Order {order.shopifyOrderNumber}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Delivery Fee</p>
              <p className="text-4xl font-bold text-uber-green">
                ${latestQuote ? (latestQuote.priceAmount / 100).toFixed(2) : '0.00'}
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div>
              <p className="text-sm text-gray-600">Deliver to:</p>
              <p className="font-medium">{order.customerName}</p>
              <p className="text-gray-600">
                {order.deliveryAddress}
                <br />
                {order.deliveryCity}, {order.deliveryState} {order.deliveryZip}
              </p>
            </div>
          </div>

          {minutesRemaining > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-yellow-800 text-center">
                <Clock className="w-4 h-4 inline mr-1" />
                Quote expires in <strong>{minutesRemaining} minutes</strong>
              </p>
            </div>
          )}

          <button
            onClick={() => approveMutation.mutate()}
            disabled={approveMutation.isPending}
            className="btn-primary w-full py-3 text-lg"
          >
            {approveMutation.isPending ? 'Processing...' : 'Approve & Book Delivery'}
          </button>

          {approveMutation.isError && (
            <p className="text-red-600 text-center mt-4">
              Failed to book delivery. Please try again.
            </p>
          )}

          <p className="text-xs text-gray-500 text-center mt-4">
            By approving, you agree to pay the delivery fee shown above.
          </p>
        </div>
      </div>
    </div>
  );
}
